import React, { Fragment, useState, useEffect } from "react";
import {
  Combobox,
  ComboboxInput,
  ComboboxButton,
  ComboboxOptions,
  ComboboxOption,
  Transition,
} from "@headlessui/react";
import {
  ChevronDownIcon,
  CheckIcon,
  RefreshCcwIcon,
} from "lucide-react";
import { listKnowledgeBases } from "../../../../service/apiService";

interface KnowledgeBase {
  id: string;
  name: string;
  index_name: string;
}

interface KnowledgeBaseSelectorProps {
  userId: string;
  refresh: number;
  onSelect: (knowledgeBase: KnowledgeBase | null) => void;
}

const KnowledgeBaseSelector: React.FC<KnowledgeBaseSelectorProps> = ({
  userId,
  refresh,
  onSelect,
}) => {
  const [selectedKnowledgeBase, setSelectedKnowledgeBase] = useState<KnowledgeBase | null>(null);
  const [query, setQuery] = useState("");
  const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBase[]>([]);
  const [isLoadingKnowledgeBases, setIsLoadingKnowledgeBases] = useState(false);

  useEffect(() => {
    const fetchKnowledgeBases = async () => {
      setIsLoadingKnowledgeBases(true);
      try {
        const result = await listKnowledgeBases();
        const mappedBases = result.map((base: any) => ({
          id: base.id,
          name: base.knowledgebase_name,
          index_name: base.index_name,
        }));
        setKnowledgeBases(mappedBases);
      } catch (error) {
        console.error("Error fetching knowledge bases:", error);
      } finally {
        setIsLoadingKnowledgeBases(false);
      }
    };

    fetchKnowledgeBases();
  }, [userId, refresh]);

  const handleKnowledgeBaseSelect = (knowledgeBase: KnowledgeBase | null) => {
    setSelectedKnowledgeBase(knowledgeBase);
    onSelect(knowledgeBase);
  };

  const filteredKnowledgeBases =
    query === ""
      ? knowledgeBases
      : knowledgeBases.filter((base) =>
          base.name
            .toLowerCase()
            .replace(/\s+/g, "")
            .includes(query.toLowerCase().replace(/\s+/g, ""))
        );

  return (
    <div className="flex flex-col">
      <label
        htmlFor="knowledgeBase"
        className="block text-sm font-medium text-gray-700"
      >
        Select BrainBank
      </label>
      <Combobox
        value={selectedKnowledgeBase}
        onChange={handleKnowledgeBaseSelect}
      >
        <div className="relative mt-1">
          <div className="relative w-full cursor-default overflow-hidden rounded-lg bg-white text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-200 focus-visible:ring-offset-2 sm:text-sm h-12">
            <ComboboxInput
              className="w-full h-full border border-gray-200 py-2 pl-3 pr-10 text-sm leading-5 text-gray-700 bg-white rounded-lg focus:outline-none focus:border-violet-500 focus:ring focus:ring-violet-200 focus:ring-opacity-20"
              displayValue={(base: KnowledgeBase) => base?.name ?? ""}
              onChange={(event) => setQuery(event.target.value)}
            />
            {isLoadingKnowledgeBases && (
              <div className="absolute inset-y-0 right-0 flex items-center pr-8">
                <RefreshCcwIcon
                  className="animate-spin h-5 w-5 text-gray-400"
                  aria-hidden="true"
                />
              </div>
            )}
            <ComboboxButton className="absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronDownIcon
                className="h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
            </ComboboxButton>
          </div>
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={() => setQuery("")}
          >
            <ComboboxOptions className="absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm z-10">
              {filteredKnowledgeBases.length === 0 && query !== "" ? (
                <div className="relative cursor-default select-none px-4 py-2 text-gray-700">
                  Nothing found.
                </div>
              ) : (
                filteredKnowledgeBases.map((base) => (
                  <ComboboxOption
                    key={base.id}
                    className={({ active }) =>
                      `relative cursor-default select-none py-2 pl-10 pr-4 ${
                        active ? "bg-violet-600 text-white" : "text-gray-900"
                      }`
                    }
                    value={base}
                  >
                    {({ selected, active }) => (
                      <>
                        <span
                          className={`block truncate ${
                            selected ? "font-medium" : "font-normal"
                          }`}
                        >
                          {base.name}
                        </span>
                        {selected ? (
                          <span
                            className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                              active ? "text-white" : "text-violet-600"
                            }`}
                          >
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        ) : null}
                      </>
                    )}
                  </ComboboxOption>
                ))
              )}
            </ComboboxOptions>
          </Transition>
        </div>
      </Combobox>
    </div>
  );
};

export default KnowledgeBaseSelector;